import {
  Enums_DocumentType,
  Reactions_Get,
  Requirements_InsertReaction,
  Requirements_Reactions,
} from "data/endpoints";
import IRepository from "data/IRepository";
import { getRouteParams } from "features/shared/helpers/routeParams";
import { useInjection } from "infrastructure/ioc/ioc.react";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import React, { useState } from "react";
import ReactionSimplifiedDTO from "../dtos/ReactionSimplifiedDTO";
import PagedDataResultDTO from "features/shared/dtos/PagedDataResultDTO";
import PageCard from "features/shared/components/card/PageCard";
import {
  getPaginationDefaults,
  getQueryParams,
} from "features/shared/helpers/queryParams";
import Pagination from "features/shared/components/pagination/Pagination";
import RequirementReaction from "../components/RequirementReaction";
import ReactionDTO from "../dtos/ReactionDTO";
import RequirementReactionDTO from "../dtos/RequirementReactionDTO";
import InsertRequirementReactionDialog from "../components/insertReactionDialog/InsertRequirementReactionDialog";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DocumentTypeEnumDTO from "features/shared/enums/dynamic/DocumentTypeEnumDTO";
import { UserEnumDTO } from "features/shared/enums/dynamic/UserEnumDTO";
import { trackPromise } from "react-promise-tracker";
import { RequirementDetail_InsertRequirementReactionDialog } from "features/shared/components/loaders/promiseLoadingAreas";
import IContentDialogService from "features/shared/components/contentDialog/IContentDialogService";
import { useQuery } from "data/hooks/useQuery";
import DataQuery from "data/DataQuery";
import NoContentWrapper from "features/shared/components/noContentWrapper/NoContentWrapper";
import RolesChecker from "features/customer/components/RolesChecker";
import CardHeaderButton from "features/shared/components/card/CardHeaderButton";
import { useTranslation } from "react-i18next";

const pageSize = 5;

interface Props {
  requirementId: string;
  requirementType: UserEnumDTO;
  addedDocumentsCount?: number;
  setAddedDocumentsCount?: React.Dispatch<React.SetStateAction<number>>;
}

const RequirementReactionsSection = ({
  requirementId,
  requirementType,
  addedDocumentsCount,
  setAddedDocumentsCount
}: Props) => {
  // translations
  const tLabels = useTranslation("requirements\\requirements").t;

  // DI
  const reactionInsertRepo = useInjection<IRepository<RequirementReactionDTO>>(IocTypes.Repository);
  const documentsTypeRepo = useInjection<IRepository<DocumentTypeEnumDTO>>(IocTypes.Repository);
  const dialogService = useInjection<IContentDialogService>(IocTypes.ContentDialogService);

  // state
  const [queryParams, setQueryParams] = useState<URLSearchParams>(getPaginationDefaults(pageSize));
  const [reactionDialogShow, setReactionDialogShow] = useState(false);
  const [documentTypes, setDocumentTypes] = useState<DocumentTypeEnumDTO[]>([]);
  const [addedReactions, setAddedReactions] = useState(0);

  // side effects
  // fetch reactions
  const query: DataQuery = {
    url: Requirements_Reactions,
    routeParams: getRouteParams("requirementId", requirementId),
    params: queryParams,
  };
  const { data: reactions, loadStatus } = useQuery<PagedDataResultDTO<ReactionSimplifiedDTO>>(
    query,
    { count: 0, result: [] },
    [requirementId, queryParams, addedReactions],
    { useTracker: false }
  );

  // handlers
  const paginationChangePageHandler = (pageNumber: number) => {
    let paramas = new URLSearchParams(queryParams);
    paramas.set("Page", pageNumber.toString());
    setQueryParams(paramas);
  };

  const openReactionHandler = async (reactionId: string) => {
    dialogService.openDialogAsync<ReactionDTO>({
      title: "Detail reakce",
      request: {
        url: Reactions_Get,
        routeParams: getRouteParams("reactionId", reactionId),
      },
      renderContent: (reaction: ReactionDTO) => (
        <div
          dangerouslySetInnerHTML={{ __html: reaction.reactionHtml || "" }}
          className="m-4"
        />
      ),
    });
  };

  const openInsertReactionDialogHandler = async () => {
    if (documentTypes.length == 0) {
      let result = await trackPromise(
        documentsTypeRepo.get({
          url: Enums_DocumentType,
          params: getQueryParams("RequirementType", requirementType.code!),
        })
      );

      setDocumentTypes(result);
    }

    setReactionDialogShow(true);
  };

  const insertReactionHandler = async (
    reaction: RequirementReactionDTO,
    files: FileList | null
  ) => {
    let response = await trackPromise(
      reactionInsertRepo.postWithFile({
        url: Requirements_InsertReaction,
        multiple: true,
        files: files,
        filesKey: "Files",
        object: reaction,
        objectKey: "Reaction",
        routeParams: getRouteParams("requirementId", requirementId),
      }),
      RequirementDetail_InsertRequirementReactionDialog
    );

    if (response.status < 300) {
      setAddedReactions(addedReactions + 1);
      if (files && files.length > 0 && setAddedDocumentsCount && addedDocumentsCount !== undefined) {
        setAddedDocumentsCount(addedDocumentsCount + files.length);
      }
    }

    setReactionDialogShow(false);
  };

  // sub-renders
  const renderReactions = () => {
    return (
      <NoContentWrapper
        data={reactions}
        loadStatus={loadStatus}
        noContentRender={() => <div className="text-muted">{tLabels("requirementReactionSection.reactionsNotFound")}</div>}
      >
        <>
          {reactions.result.map((item, index) => (
            <div className="py-1" key={index}>
              <RequirementReaction
                reaction={item}
                openReaction={openReactionHandler}
              />
            </div>
          ))}
          {renderPagination()}
        </>
      </NoContentWrapper>
    );
  };

  const renderPagination = () => {
    if (reactions.count > pageSize) {
      return (
        <>
          <hr />
          <div className="d-flex justify-content-center pb-1">
            <Pagination
              urlParams={queryParams}
              count={reactions.count}
              pageSize={pageSize}
              onPageChange={paginationChangePageHandler}
              useXsView
            />
          </div>
        </>
      );
    }
  }

  const renderActionBtns = () => {
    let result: JSX.Element[] = [];
    let elem = (
      <RolesChecker enabledRoles="Contributor">
        <CardHeaderButton
          type="button"
          title={tLabels("requirementReactionSection.insertReaction")}
          onClick={openInsertReactionDialogHandler}
          key={requirementId}
        >
          <FontAwesomeIcon icon="plus" />
        </CardHeaderButton>
      </RolesChecker>
    );

    result.push(elem);
    return result;
  };

  // render
  return (
    <PageCard title={tLabels("requirementReactionSection.cardTitle")} headerActionBtns={renderActionBtns}>
      {renderReactions()}
      <InsertRequirementReactionDialog
        show={reactionDialogShow}
        setShow={setReactionDialogShow}
        onSubmit={insertReactionHandler}
        documentTypes={documentTypes}
      />
    </PageCard>
  );
};

export default RequirementReactionsSection;
